import { useState } from 'react'
import { updateDoc, doc } from 'firebase/firestore'
import { useToast } from '@chakra-ui/react'
import { Occupation } from '../../../../../../types/FirestoreUser'
import { getEffectiveServices } from '../../../../../../utils/firebaseServiceContext'

export const useSaveOccupation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()

  // Validation helper function
  const validateOccupation = (occupation: Occupation): string | null => {
    if (!occupation.occupationName?.trim()) {
      return 'Occupation name is required'
    }

    // BasicDateStringPicker returns values like "0-0-0", "2021-0-0", "2021-10-0" when incomplete
    const isStartDateEmpty =
      !occupation.startDate?.trim() ||
      occupation.startDate.startsWith('0-') ||
      occupation.startDate.includes('-0-') ||
      occupation.startDate.endsWith('-0')
    if (isStartDateEmpty) {
      return 'Start date is required'
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(occupation.startDate)) {
      return 'Start date must be in YYYY-MM-DD format'
    }

    const startDate = new Date(occupation.startDate)
    if (isNaN(startDate.getTime())) {
      return 'Start date must be a valid date'
    }

    // BasicDateStringPicker returns values like "0-0-0", "2021-0-0", "2021-10-0" when incomplete
    const isEndDateEmpty =
      !occupation.endDate?.trim() ||
      occupation.endDate.startsWith('0-') ||
      occupation.endDate.includes('-0-') ||
      occupation.endDate.endsWith('-0')
    if (!isEndDateEmpty && occupation.endDate) {
      if (!dateRegex.test(occupation.endDate)) {
        return 'End date must be in YYYY-MM-DD format'
      }

      const endDate = new Date(occupation.endDate)
      if (isNaN(endDate.getTime())) {
        return 'End date must be a valid date'
      }

      if (endDate <= startDate) {
        return 'End date must be after start date'
      }
    }

    if (!occupation.stressLevel?.trim()) {
      return 'Stress level is required'
    }

    return null
  }

  const saveOccupation = async (
    userId: string,
    occupation: Occupation,
    existingOccupations: Occupation[] = [],
    isEditing: boolean = false
  ) => {
    setIsLoading(true)
    try {
      // Validate occupation data
      const validationError = validateOccupation(occupation)
      if (validationError) {
        // Just throw the error without logging to console
        throw new Error(validationError)
      }

      let updatedOccupations: Occupation[]

      if (isEditing) {
        // Update existing occupation
        updatedOccupations = existingOccupations.map((existingOccupation) =>
          existingOccupation.id === occupation.id
            ? {
                id: occupation.id,
                occupationName: occupation.occupationName,
                startDate: occupation.startDate,
                stressLevel: occupation.stressLevel,
                ...(occupation.endDate && { endDate: occupation.endDate }),
              }
            : existingOccupation
        )
      } else {
        // Add new occupation with generated ID
        const newOccupation: Occupation = {
          id: Date.now().toString(),
          occupationName: occupation.occupationName,
          startDate: occupation.startDate,
          stressLevel: occupation.stressLevel,
          ...(occupation.endDate && { endDate: occupation.endDate }),
        }
        updatedOccupations = [...existingOccupations, newOccupation]
      }

      const userRef = doc(
        getEffectiveServices().firestore,
        'users',
        userId,
      )
      await updateDoc(userRef, {
        'additionalDetails.occupations': updatedOccupations,
      })

      toast({
        title: isEditing ? 'Occupation updated' : 'Occupation added',
        description: isEditing
          ? 'Your occupation has been successfully updated.'
          : 'Your occupation has been successfully added.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      return updatedOccupations
    } catch (error) {
      // Check if this is a validation error from our validateOccupation function
      const isValidationError = validateOccupation(occupation) !== null

      // Only log errors for non-validation issues (server errors, etc.)
      if (!isValidationError) {
        console.error('Error saving occupation:', error)

        toast({
          title: 'Error',
          description: isEditing
            ? 'Failed to update occupation. Please try again.'
            : 'Failed to add occupation. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }

      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return { saveOccupation, isLoading }
}
